import React, { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { useParams } from "react-router-dom";
import axios from "axios";
import { PublicNav, PublicFooter } from "@/components/PublicChrome";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldX, Search } from "lucide-react";

const fmt = (iso) => fmtDate(iso);

export default function VerifyCertificate() {
  const { certNumber } = useParams();
  const [number, setNumber] = useState(certNumber || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (n) => {
    if (!n) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/verify/${encodeURIComponent(n.trim())}`);
      setResult(data);
    } catch { setResult({ valid: false }); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (certNumber) check(certNumber); }, [certNumber]);

  return (
    <div className="min-h-screen bg-cream font-body" data-testid="verify-page">
      <PublicNav />
      <section className="max-w-2xl mx-auto px-5 md:px-8 pt-16 pb-20 text-left">
        <span className="label-caps text-coral">Certificate verification</span>
        <h1 className="text-4xl md:text-5xl font-bold text-brand tracking-tight mt-3">Check a KOHC certificate</h1>
        <p className="text-lg text-muted-foreground mt-4">Inspectors and managers can confirm any certificate issued by Knowledge Oral Health Care. Scan the QR code on the certificate or enter the certificate number below.</p>
        <form onSubmit={(e) => { e.preventDefault(); check(number); }} className="mt-8 flex gap-2">
          <Input data-testid="verify-input" placeholder="KOHC-XXXXXXXX" value={number} onChange={(e) => setNumber(e.target.value)} className="h-11 font-mono uppercase" />
          <Button data-testid="verify-btn" type="submit" disabled={loading} className="h-11 rounded-full bg-brand hover:bg-brand-hover text-white px-6"><Search className="h-4 w-4 mr-2" />{loading ? "Checking…" : "Verify"}</Button>
        </form>

        {result && (
          result.valid ? (
            <div data-testid="verify-valid" className="mt-8 rounded-3xl bg-white border border-rag-green/30 p-6 md:p-8">
              <div className="flex items-center gap-3 text-rag-green"><ShieldCheck className="h-8 w-8" /><span className="font-heading font-bold text-xl">Genuine certificate</span></div>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div><div className="text-muted-foreground">Awarded to</div><div className="font-heading font-bold text-brand text-lg">{result.recipient}</div></div>
                <div><div className="text-muted-foreground">Certificate number</div><div className="font-mono font-semibold">{result.cert_number}</div></div>
                <div className="sm:col-span-2"><div className="text-muted-foreground">Course</div><div className="font-semibold text-brand">{result.course_title}</div></div>
                <div><div className="text-muted-foreground">Type</div><div className="font-medium">{result.kind}{result.cpd_hours ? ` · ${result.cpd_hours} hrs CPD` : ""}</div></div>
                <div><div className="text-muted-foreground">Issued on</div><div className="font-medium">{fmt(result.issued_at)}</div></div>
                {result.care_home && <div><div className="text-muted-foreground">Care home</div><div className="font-medium flex items-center gap-2">{result.care_home_logo && <img src={result.care_home_logo} alt="" className="h-6 object-contain" />}{result.care_home}</div></div>}
                <div><div className="text-muted-foreground">Issued by</div><div className="font-medium">{result.issuer}</div></div>
              </div>
            </div>
          ) : (
            <div data-testid="verify-invalid" className="mt-8 rounded-3xl bg-white border border-rag-red/30 p-6 md:p-8">
              <div className="flex items-center gap-3 text-rag-red"><ShieldX className="h-8 w-8" /><span className="font-heading font-bold text-xl">No certificate found</span></div>
              <p className="text-sm text-muted-foreground mt-3">We couldn't find a certificate with that number. Check the number carefully, or contact us at info@kohc.co.uk.</p>
            </div>
          )
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
